import type { ClusterResult, FractureSurfaceFrame, StageResult, TimeStep } from './types';

export type PerforationPosition = {
  md: number;
  tvd?: number;
  x?: number;
  y?: number;
  z?: number;
};

export type SimulationPlaybackCluster = {
  clusterId: string;
  clusterIndex: number;
  stageId: number | string;
  isInitiated: boolean;
  perforation: PerforationPosition;
  lengthM: number;
  leftWingLengthM: number;
  rightWingLengthM: number;
  heightM: number;
  upperHeightM?: number;
  lowerHeightM?: number;
  actualWidthM: number;
  actualWidthMm: number;
  hasWidthResult: boolean;
  fracturePressureMpa: number;
  hasFracturePressureResult: boolean;
  netPressureMpa: number;
  hasNetPressureResult: boolean;
  flowRateM3s: number;
  stressShadowMpa: number;
  localShminMpa: number;
  proppantConcentrationKgm3?: number;
  proppantType?: string;
  fluidType?: string;
  proppedWidthM?: number;
  conductivity?: number;
  pumpingStage?: string;
  stageRateM3min?: number;
  bottomholePressureMpa?: number;
  cumulativeFluidM3?: number;
  cumulativeProppantKg?: number;
  bottomholeProppantConcentrationKgm3?: number;
  pressureReferenceLocation?: string;
  concentrationDefinition?: string;
  surfaceFrame?: FractureSurfaceFrame;
  hasSurfaceGeometry?: boolean;
};

export type SimulationPlaybackFrame = {
  timeS: number;
  stageId?: number | string;
  stageIndex?: number;
  timelineTimeS?: number;
  clusters: SimulationPlaybackCluster[];
};

export type SimulationPlayback = {
  frames: SimulationPlaybackFrame[];
  durationS: number;
  hasTimeSeries: boolean;
};

type RawFrame = TimeStep & Record<string, unknown>;

export type CommittedClusterFrame = {
  clusterId: string;
  clusterIndex: number;
  stageId: number | string;
  perforation: PerforationPosition;
  timeStep: RawFrame;
  profileFrame?: Record<string, unknown> | null;
  surfaceFrame?: FractureSurfaceFrame | null;
  solverDiagnostics?: Record<string, unknown>;
};

export type CommittedSimulationFrame = {
  time: number;
  totalTime?: number;
  stageId: number | string;
  step: number;
  globalStep: number;
  totalGlobalSteps: number;
  progress: number;
  stageIndex?: number;
  stageCount?: number;
  stageSequenceDirection?: 'TOE_TO_HEEL_MD_DESCENDING';
  performance?: Record<string, number | boolean>;
  plannedClusters?: Array<Pick<CommittedClusterFrame, 'clusterId' | 'clusterIndex' | 'stageId' | 'perforation'>>;
  clusters: CommittedClusterFrame[];
};

const finite = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nonNegative = (value: unknown, fallback = 0): number =>
  Math.max(0, finite(value, fallback));

const frameTime = (frame: RawFrame): number =>
  nonNegative(frame.time_s ?? frame.time ?? frame.t_s);

const firstFinite = (source: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = Number(source[key]);
    if (Number.isFinite(value)) return value;
  }
  return undefined;
};

const pressureMpa = (source: Record<string, unknown>, mpaKeys: string[], paKeys: string[]): number => {
  const mpa = firstFinite(source, mpaKeys);
  if (mpa !== undefined) return nonNegative(mpa);
  const pa = firstFinite(source, paKeys);
  return nonNegative(pa === undefined ? 0 : pa / 1e6);
};

const widthM = (source: Record<string, unknown>): number => {
  const metres = firstFinite(source, ['width_m', 'width']);
  if (metres !== undefined) return nonNegative(metres);
  return nonNegative(firstFinite(source, ['width_mm', 'w0_mm']) ?? 0) / 1000;
};

const lengthM = (source: Record<string, unknown>): number =>
  nonNegative(firstFinite(source, ['half_length_m', 'length_m', 'half_length', 'length', 'L_m']) ?? 0);

const heightM = (source: Record<string, unknown>): number =>
  nonNegative(firstFinite(source, ['height_m', 'height', 'h_m']) ?? 0);

const clusterFrames = (cluster: ClusterResult): RawFrame[] => {
  const frames = [...(cluster.time_steps ?? cluster.frames ?? [])]
    .map((frame) => frame as RawFrame)
    .filter((frame) => Number.isFinite(frameTime(frame)))
    .sort((a, b) => frameTime(a) - frameTime(b));
  const surfaceFrames = [...(cluster.surface_frames ?? [])]
    .filter((frame) => Number.isFinite(Number(frame.time)))
    .sort((a, b) => Number(a.time) - Number(b.time));
  const finalSurfaceFrame = cluster.fracture_surface_frame;
  if (finalSurfaceFrame && Number.isFinite(Number(finalSurfaceFrame.time))
    && !surfaceFrames.some((frame) => Math.abs(Number(frame.time) - Number(finalSurfaceFrame.time)) < 1e-9)) {
    surfaceFrames.push(finalSurfaceFrame);
    surfaceFrames.sort((a, b) => Number(a.time) - Number(b.time));
  }
  if (!surfaceFrames.length) return frames;
  let surfaceIndex = -1;
  return frames.map((frame) => {
    const time = frameTime(frame);
    while (surfaceIndex + 1 < surfaceFrames.length
      && Number(surfaceFrames[surfaceIndex + 1].time) <= time + 1e-9) surfaceIndex += 1;
    return surfaceIndex >= 0
      ? { ...frame, fracture_surface_frame: surfaceFrames[surfaceIndex] }
      : frame;
  });
};

const clusterIdentity = (cluster: ClusterResult, stage: StageResult): SimulationPlaybackCluster => {
  const source = cluster as ClusterResult & Record<string, unknown>;
  const stageId = cluster.stage_id ?? stage.stage_number;
  const clusterIndex = Math.max(0, Math.trunc(finite(cluster.cluster_index)));
  const clusterId = cluster.cluster_id || `S${String(stageId)}-C${clusterIndex + 1}`;
  const md = finite(cluster.cluster_position_md, stage.perf_md);
  return {
    clusterId,
    clusterIndex,
    stageId,
    isInitiated: false,
    perforation: { md },
    lengthM: 0,
    leftWingLengthM: 0,
    rightWingLengthM: 0,
    heightM: 0,
    actualWidthM: 0,
    actualWidthMm: 0,
    hasWidthResult: false,
    fracturePressureMpa: pressureMpa(source, ['fracture_pressure_mpa'], ['fracture_pressure_pa']),
    hasFracturePressureResult: firstFinite(source, ['fracture_pressure_mpa', 'fracture_pressure_pa']) !== undefined,
    netPressureMpa: pressureMpa(source, ['net_pressure_mpa'], ['net_pressure_pa']),
    hasNetPressureResult: firstFinite(source, ['net_pressure_mpa', 'net_pressure_pa']) !== undefined,
    flowRateM3s: 0,
    stressShadowMpa: 0,
    localShminMpa: pressureMpa(source, ['local_shmin_mpa'], ['local_shmin_pa']),
    proppantType: typeof source.proppant_type === 'string' ? source.proppant_type : undefined,
    fluidType: typeof source.fluid_type === 'string' ? source.fluid_type : undefined,
    conductivity: firstFinite(source, ['conductivity']),
    hasSurfaceGeometry: Boolean(cluster.fracture_surface_frame || cluster.surface_frames?.length),
  };
};

const stateFromSource = (
  identity: SimulationPlaybackCluster,
  source: Record<string, unknown>,
): SimulationPlaybackCluster => {
  const length = lengthM(source);
  const left = nonNegative(firstFinite(source, ['left_wing_length_m', 'left_wing_length']) ?? length);
  const right = nonNegative(firstFinite(source, ['right_wing_length_m', 'right_wing_length']) ?? length);
  const height = heightM(source);
  const actualWidthM = widthM(source);
  const hasWidthResult = firstFinite(source, ['width_m', 'width', 'width_mm', 'w0_mm']) !== undefined;
  const hasFracturePressureResult = firstFinite(
    source,
    ['fracture_pressure_mpa', 'fracture_pressure_pa', 'fracture_pressure'],
  ) !== undefined;
  const hasNetPressureResult = firstFinite(source, ['net_pressure_mpa', 'net_pressure_pa']) !== undefined;
  const initiatedValue = source.is_initiated;
  const isInitiated = typeof initiatedValue === 'boolean'
    ? initiatedValue
    : length > 0 || actualWidthM > 0;
  const upperHeight = firstFinite(source, ['upper_height_m', 'top_height_m', 'height_growth_up']);
  const lowerHeight = firstFinite(source, ['lower_height_m', 'bottom_height_m', 'height_growth_down']);
  return {
    ...identity,
    isInitiated,
    lengthM: isInitiated ? length : 0,
    leftWingLengthM: isInitiated ? left : 0,
    rightWingLengthM: isInitiated ? right : 0,
    heightM: isInitiated ? height : 0,
    upperHeightM: isInitiated && upperHeight !== undefined ? nonNegative(upperHeight) : undefined,
    lowerHeightM: isInitiated && lowerHeight !== undefined ? nonNegative(lowerHeight) : undefined,
    actualWidthM: isInitiated ? actualWidthM : 0,
    actualWidthMm: isInitiated ? actualWidthM * 1000 : 0,
    hasWidthResult,
    fracturePressureMpa: pressureMpa(source, ['fracture_pressure_mpa'], ['fracture_pressure_pa', 'fracture_pressure']),
    hasFracturePressureResult,
    netPressureMpa: pressureMpa(source, ['net_pressure_mpa'], ['net_pressure_pa']),
    hasNetPressureResult,
    flowRateM3s: nonNegative(firstFinite(source, ['flow_rate_m3s', 'flow_rate', 'allocated_rate_m3s']) ?? 0),
    stressShadowMpa: pressureMpa(source, ['stress_shadow_mpa'], ['stress_shadow_pa', 'stress_shadow']),
    localShminMpa: pressureMpa(source, ['local_shmin_mpa'], ['local_shmin_pa', 'local_shmin']),
    proppantConcentrationKgm3: firstFinite(source, ['proppant_concentration_kgm3', 'sand_conc_kgm3']),
    proppantType: typeof source.proppant_type === 'string' ? source.proppant_type : identity.proppantType,
    fluidType: typeof source.fluid_type === 'string' ? source.fluid_type : identity.fluidType,
    proppedWidthM: firstFinite(source, ['propped_width_m'])
      ?? (firstFinite(source, ['propped_width_mm']) === undefined
        ? identity.proppedWidthM
        : nonNegative(firstFinite(source, ['propped_width_mm'])) / 1000),
    conductivity: firstFinite(source, ['conductivity']) ?? identity.conductivity,
    pumpingStage: typeof source.pumping_stage === 'string' ? source.pumping_stage : identity.pumpingStage,
    stageRateM3min: firstFinite(source, ['stage_flow_rate_m3min', 'rate_m3min']),
    bottomholePressureMpa: firstFinite(source, ['bottomhole_pressure_mpa'])
      ?? (firstFinite(source, ['wellbore_pressure_pa']) === undefined
        ? identity.bottomholePressureMpa
        : nonNegative(firstFinite(source, ['wellbore_pressure_pa'])) / 1e6),
    cumulativeFluidM3: firstFinite(source, ['stage_cumulative_fluid_m3', 'Vi_m3', 'injected_volume_m3'])
      ?? identity.cumulativeFluidM3,
    cumulativeProppantKg: firstFinite(source, [
      'cluster_cumulative_proppant_kg', 'stage_cumulative_proppant_kg', 'cumulative_proppant_kg',
    ]) ?? identity.cumulativeProppantKg,
    bottomholeProppantConcentrationKgm3: firstFinite(source, [
      'bottomhole_proppant_concentration_kgm3', 'sand_conc_kgm3',
    ]) ?? identity.bottomholeProppantConcentrationKgm3,
    pressureReferenceLocation: typeof source.pressure_reference_location === 'string'
      ? source.pressure_reference_location : identity.pressureReferenceLocation,
    concentrationDefinition: typeof source.concentration_definition === 'string'
      ? source.concentration_definition : identity.concentrationDefinition,
    surfaceFrame: (
      source.fracture_surface_frame && typeof source.fracture_surface_frame === 'object'
        ? source.fracture_surface_frame as FractureSurfaceFrame
        : identity.surfaceFrame
    ),
    hasSurfaceGeometry: identity.hasSurfaceGeometry,
  };
};

const sampleCluster = (
  identity: SimulationPlaybackCluster,
  frames: RawFrame[],
  timeS: number,
): SimulationPlaybackCluster => {
  if (!frames.length || timeS <= 0) return identity;
  const firstTime = frameTime(frames[0]);
  if (timeS < firstTime) {
    return identity;
  }
  let previous = frames[0];
  for (let index = 1; index < frames.length; index += 1) {
    const next = frames[index];
    const nextTime = frameTime(next);
    if (timeS < nextTime) {
      return stateFromSource(identity, previous);
    }
    if (Math.abs(timeS - nextTime) < 1e-9) return stateFromSource(identity, next);
    previous = next;
  }
  return stateFromSource(identity, frames[frames.length - 1]);
};

const stageClusters = (stage: StageResult): ClusterResult[] => {
  if (stage.clusters?.length) return stage.clusters;
  return [{
    cluster_index: 0,
    cluster_id: `S${stage.stage_number}-C1`,
    stage_id: stage.stage_number,
    cluster_position_md: stage.perf_md,
    half_length: stage.half_length,
    height: stage.height,
    w0_mm: stage.w0_mm,
    net_pressure_mpa: stage.net_pressure_mpa,
    efficiency_pct: stage.efficiency_pct,
    time_steps: stage.time_steps,
  }];
};

export const buildSimulationPlayback = (stages: StageResult[]): SimulationPlayback => {
  const treatmentOrder = [...stages].sort((left, right) => {
    const stageMd = (stage: StageResult): number => {
      const representativeMd = Number(stage.perf_md);
      if (Number.isFinite(representativeMd)) return representativeMd;
      const candidates = [stage.top_perf, stage.bottom_perf]
        .map(Number)
        .filter(Number.isFinite);
      (stage.clusters ?? []).forEach((cluster) => {
        const value = Number(cluster.cluster_position_md);
        if (Number.isFinite(value)) candidates.push(value);
      });
      return candidates.length ? Math.max(...candidates) : Number.NEGATIVE_INFINITY;
    };
    return stageMd(right) - stageMd(left) || right.stage_number - left.stage_number;
  });
  const stageInputs = treatmentOrder.map((stage) => ({
    stage,
    clusters: stageClusters(stage).map((cluster) => ({
      identity: clusterIdentity(cluster, stage),
      frames: clusterFrames(cluster),
    })),
  })).filter((stage) => stage.clusters.length > 0);
  if (!stageInputs.length) return { frames: [], durationS: 0, hasTimeSeries: false };

  const hasTimeSeries = stageInputs.some((stage) => stage.clusters.some(({ frames }) => frames.length > 0));
  const frames: SimulationPlaybackFrame[] = [];
  let timelineOffsetS = 0;
  stageInputs.forEach((active, activeIndex) => {
    const localTimes = new Set<number>([0]);
    active.clusters.forEach(({ frames: clusterTimeSteps }) => (
      clusterTimeSteps.forEach((frame) => localTimes.add(frameTime(frame)))
    ));
    const orderedLocalTimes = [...localTimes].filter(Number.isFinite).sort((a, b) => a - b);
    orderedLocalTimes.forEach((localTimeS) => {
      if (activeIndex > 0 && localTimeS === 0) return;
      frames.push({
        timeS: localTimeS,
        timelineTimeS: timelineOffsetS + localTimeS,
        stageId: active.stage.stage_number,
        stageIndex: activeIndex,
        clusters: stageInputs.flatMap((stageInput, stageIndex) => stageInput.clusters.map(({ identity, frames: clusterTimeSteps }) => {
          if (stageIndex < activeIndex) return sampleCluster(identity, clusterTimeSteps, Number.POSITIVE_INFINITY);
          if (stageIndex > activeIndex) return identity;
          return sampleCluster(identity, clusterTimeSteps, localTimeS);
        })),
      });
    });
    timelineOffsetS += orderedLocalTimes[orderedLocalTimes.length - 1] ?? 0;
  });
  return {
    frames,
    durationS: hasTimeSeries ? frames[frames.length - 1]?.timelineTimeS ?? 0 : 0,
    hasTimeSeries,
  };
};

export const getPlaybackFrameAtTime = (
  frames: SimulationPlaybackFrame[],
  requestedTimeS: number,
): SimulationPlaybackFrame | null => {
  if (!frames.length) return null;
  const timelineOf = (frame: SimulationPlaybackFrame) => frame.timelineTimeS ?? frame.timeS;
  const timeS = Math.min(timelineOf(frames[frames.length - 1]), Math.max(0, finite(requestedTimeS)));
  if (timeS <= timelineOf(frames[0])) return frames[0];
  for (let index = 1; index < frames.length; index += 1) {
    const next = frames[index];
    const nextTimeS = timelineOf(next);
    if (Math.abs(timeS - nextTimeS) < 1e-9) return next;
    if (timeS < nextTimeS) {
      const previous = frames[index - 1];
      return previous;
    }
  }
  return frames[frames.length - 1];
};

const committedIdentity = (
  cluster: Pick<CommittedClusterFrame, 'clusterId' | 'clusterIndex' | 'stageId' | 'perforation'>
    & Partial<Pick<CommittedClusterFrame, 'surfaceFrame'>>,
  previous?: SimulationPlaybackCluster,
): SimulationPlaybackCluster => previous ?? {
  clusterId: cluster.clusterId,
  clusterIndex: cluster.clusterIndex,
  stageId: cluster.stageId,
  isInitiated: false,
  perforation: cluster.perforation,
  lengthM: 0,
  leftWingLengthM: 0,
  rightWingLengthM: 0,
  heightM: 0,
  actualWidthM: 0,
  actualWidthMm: 0,
  hasWidthResult: false,
  fracturePressureMpa: 0,
  hasFracturePressureResult: false,
  netPressureMpa: 0,
  hasNetPressureResult: false,
  flowRateM3s: 0,
  stressShadowMpa: 0,
  localShminMpa: 0,
  hasSurfaceGeometry: Boolean(cluster.surfaceFrame),
};

/** Convert one server-committed timestep without interpolation or mesh synthesis. */
export const buildCommittedPlaybackFrame = (
  committed: CommittedSimulationFrame,
  previous: SimulationPlaybackFrame | null,
): SimulationPlaybackFrame => {
  const byId = new Map((previous?.clusters ?? []).map((cluster) => [cluster.clusterId, cluster]));
  (committed.plannedClusters ?? []).forEach((cluster) => {
    if (byId.has(cluster.clusterId)) return;
    byId.set(cluster.clusterId, committedIdentity(cluster));
  });
  committed.clusters.forEach((cluster) => {
    const prior = byId.get(cluster.clusterId);
    const identity = committedIdentity(cluster, prior);
    const source: Record<string, unknown> = {
      ...cluster.timeStep,
      ...(cluster.surfaceFrame ? { fracture_surface_frame: cluster.surfaceFrame } : {}),
    };
    const next = stateFromSource(identity, source);
    byId.set(cluster.clusterId, {
      ...next,
      surfaceFrame: cluster.surfaceFrame ?? undefined,
      hasSurfaceGeometry: Boolean(cluster.surfaceFrame),
    });
  });
  const previousStageChanged = previous?.stageId !== undefined
    && String(previous.stageId) !== String(committed.stageId);
  const timelineBase = previousStageChanged
    ? (previous?.timelineTimeS ?? previous?.timeS ?? 0)
    : (previous?.timelineTimeS ?? previous?.timeS ?? 0) - (previous?.timeS ?? 0);
  return {
    timeS: nonNegative(committed.time),
    stageId: committed.stageId,
    stageIndex: committed.stageIndex,
    timelineTimeS: Math.max(0, timelineBase + nonNegative(committed.time)),
    clusters: [...byId.values()].sort((left, right) => (
      String(left.stageId).localeCompare(String(right.stageId), undefined, { numeric: true })
      || left.clusterIndex - right.clusterIndex
    )),
  };
};

export const appendCommittedPlaybackFrame = (
  frames: SimulationPlaybackFrame[],
  committed: CommittedSimulationFrame,
  maximum = 240,
): SimulationPlaybackFrame[] => {
  const next = buildCommittedPlaybackFrame(committed, frames[frames.length - 1] ?? null);
  return [...frames, next].slice(-Math.max(1, maximum));
};
